import React from 'react';
import { Link } from 'react-router-dom';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { ShoppingCart, Bot, CreditCard, ArrowRight, MessageCircle } from 'lucide-react';
import { HERO_STATS, VENDDI_COLORS } from '@/data/venddi';

export default function VenddiHero() {
  const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.25 });

  return (
    <div className="relative overflow-hidden bg-[#050E1F] text-white">
      <div className="absolute inset-0 noise-texture opacity-40" />
      <div className="absolute -top-40 right-0 w-[600px] h-[600px] rounded-full bg-[#0EA5E9]/20 blur-3xl" />
      <div className="absolute -bottom-40 -left-20 w-[600px] h-[600px] rounded-full bg-[#38BDF8]/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-12 items-center min-w-0">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0EA5E9] bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 rounded-full">
              <ShoppingCart className="w-3.5 h-3.5" />SOFTWARE POS · SELF-SERVICE KIOSKS
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 ml-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#38BDF8] bg-[#38BDF8]/10 border border-[#38BDF8]/20 rounded-full">
              🚀 NUEVO PRODUCTO · VISIÓN DE FUTURO
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02] mb-5" style={{ fontFamily: 'Syne,sans-serif' }}>
              Venddi<span className="text-xl align-super text-[#0EA5E9]">™</span> POS<br />
              <span className="text-[#0EA5E9]">Vende más</span>.<br />
              <span className="text-[#38BDF8]">Con menos fricción.</span><br />
              Con menos horas hombre.
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-7 max-w-xl">
              El software POS chileno diseñado 100% para kioscos de autoservicio. Conectado al SII, a tu ERP y a todos los medios de pago del país. Orquestado por Kian™, nuestra IA de operaciones.
            </p>

            <div className="flex flex-wrap gap-3 mb-10">
              <a href="#planes" className="inline-flex items-center gap-2 px-5 py-3.5 rounded-xl bg-[#38BDF8] text-[#0A2540] text-sm font-bold hover:bg-sky-300 shadow-lg shadow-sky-500/30 transition-all" data-testid="hero-planes-cta">
                <CreditCard className="w-4 h-4" />Ver planes desde 1 UF/mes
              </a>
              <Link to="/contacto?source=venddi-demo" className="inline-flex items-center gap-2 px-5 py-3.5 rounded-xl bg-white/10 backdrop-blur text-white text-sm font-semibold hover:bg-white/20 border border-white/20 transition-all" data-testid="hero-demo-cta">
                Agenda demo <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="https://wa.me/56975294340" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3.5 rounded-xl text-emerald-400 border border-emerald-400/30 text-sm font-semibold hover:bg-emerald-400/10 transition-all">
                <MessageCircle className="w-4 h-4" />WhatsApp
              </a>
            </div>

            {/* STATS — reworked for punch + alignment, fits all viewports */}
            <div ref={statsRef} className="grid grid-cols-3 gap-2 sm:gap-4 w-full">
              {HERO_STATS.map((k, i) => (
                <div key={i} className="relative min-w-0 p-3 sm:p-4 lg:p-5 rounded-2xl bg-white/5 backdrop-blur border border-white/10 hover:border-[#38BDF8]/40 transition-all">
                  <div className="flex items-baseline gap-0.5 leading-none min-w-0 tracking-tighter" style={{ fontFamily: 'Syne,sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                    {k.prefix && <span className="text-base sm:text-xl lg:text-2xl font-black text-[#38BDF8]">{k.prefix}</span>}
                    <span className="text-xl sm:text-3xl lg:text-4xl font-black text-white">
                      {statsInView ? <CountUp end={k.v} duration={2.2} decimals={k.decimals || 0} separator="." decimal="," /> : '0'}
                    </span>
                    <span className="text-sm sm:text-lg lg:text-xl font-black text-[#38BDF8]">{k.suffix}</span>
                  </div>
                  <div className="text-[9px] sm:text-[10px] lg:text-[11px] text-slate-400 uppercase tracking-[0.10em] mt-2 sm:mt-3 font-semibold leading-tight">{k.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Kiosco real Venddi Software — foto en operación */}
          <div className="relative min-w-0">
            <div className="absolute -inset-6 bg-gradient-to-br from-[#0EA5E9]/30 via-[#38BDF8]/15 to-transparent blur-3xl rounded-full" />
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
              <img
                src="/venddi-kiosco-hero.png"
                alt="Kiosco de autoservicio Venddi Software en operación — restaurante QSR"
                loading="eager"
                className="w-full h-auto block"
                style={{ aspectRatio: '1/1', objectFit: 'cover' }}
              />
              {/* Top-right floating badge — Kian vigilando */}
              <div className="absolute top-3 right-3 sm:top-5 sm:right-5 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#050E1F]/80 backdrop-blur-md border border-white/10 shadow-lg">
                <span className="relative flex items-center justify-center w-2 h-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
                  <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400" />
                </span>
                <Bot className="w-3.5 h-3.5 text-[#38BDF8]" />
                <span className="text-[10px] font-bold text-white uppercase tracking-wider">Kian vigilando</span>
              </div>
              {/* Bottom-left floating chip — Kian sugiere */}
              <div className="hidden sm:flex absolute bottom-5 left-5 items-center gap-2 max-w-[260px] p-2.5 rounded-xl bg-white/95 backdrop-blur-md border border-white/60 shadow-xl">
                <div className="w-8 h-8 rounded-lg bg-[#0A2540] flex items-center justify-center shrink-0">
                  <Bot className="w-4 h-4 text-[#38BDF8]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[9px] font-bold text-[#0A2540] uppercase tracking-wider">Kian sugiere</div>
                  <div className="text-[11px] text-slate-700 leading-snug font-medium">¿Agregas combo por $500 menos?</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
